import React, { useCallback, useMemo } from "react";
import { FieldArray, FieldArrayRenderProps, Formik } from "formik";
import { Col, Grid, Row } from "rsuite";
import {
  AccessControl,
  Button,
  ErrorMessageField,
  HorizontalDivider,
  Icon,
  Input,
  SelectPickerInput,
} from "@shared/components";
import { Alert, User } from "@shared/models";
import { AlertFormShape, CreateAlertDto } from "@containers/Alert/interfaces";
import {
  ALERT_COMPARISON_TYPE_OPTIONS,
  ALERT_SIGNAL_TYPE_OPTIONS,
  ALERT_TYPE_OPTIONS,
  ALERT_VALUE_TYPE_OPTIONS,
  MODAL_TYPES,
  PERMISSION,
} from "@shared/constants";
import { useCheckPermission } from "@shared/hooks";
import { actions as sharedActions } from "@shared/store";
import { useDispatch } from "react-redux";
import classnames from "classnames";

import { getInitValues, prepareSubmitValue, createValidationSchemaSchema } from "./formHelpers";

import "./index.scss";

interface AlertFormProps {
  userId: number | null;
  alertList: Alert[];
  authorizedUser: User | null;
  onChangeForm: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: CreateAlertDto["alert_settings"]) => void;
}

const AlertForm: React.FunctionComponent<AlertFormProps> = (props) => {
  const dispatch = useDispatch();
  const { userId, alertList, onChangeForm, onCloseForm, onSubmitForm } = props;

  const formValues = useMemo(() => getInitValues(alertList), [alertList]);
  const canCreateEditAlert = useCheckPermission(
    userId ? PERMISSION.MANAGE_CUSTOM_ALERTS : PERMISSION.MANAGE_HOSPITALS_ALERTS,
  );

  const addNewAlert = useCallback(
    (arrayHelpers: FieldArrayRenderProps) => {
      arrayHelpers.push({
        value_type: ALERT_VALUE_TYPE_OPTIONS[0].value,
        signal_type: ALERT_SIGNAL_TYPE_OPTIONS[0].value,
        alert_type: ALERT_TYPE_OPTIONS[0].value,
        comparison_type: ALERT_COMPARISON_TYPE_OPTIONS[0].value,
        minimal_count: 5,
        value: 50,
        user_id: userId,
      });
    },
    [userId],
  );

  const onDelete = useCallback(
    (arrayHelpers: FieldArrayRenderProps, idx: number) => {
      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: "Delete Notification",
            content: `Are you sure you want to delete this notification?`,
            cancelBtnText: "Cancel",
            successBtnText: "Delete",
            onSuccess: () => {
              arrayHelpers.remove(idx);
            },
          },
        }),
      );
    },
    [dispatch],
  );

  const valueTypeOnChangeHandler = useCallback(
    (
      value: string,
      values: AlertFormShape,
      i: number,
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    ) => {
      if (value === ALERT_VALUE_TYPE_OPTIONS[1].value && values.alert_settings[i].value > 10) {
        setFieldValue(`alert_settings.${i}.value`, 5);
      }

      setFieldValue(`alert_settings.${i}.value_type`, value);
    },
    [],
  );

  return (
    <Formik
      validationSchema={createValidationSchemaSchema}
      enableReinitialize={true}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting }) => {
        onSubmitForm(prepareSubmitValue(values, userId));
        setSubmitting(false);
      }}
      initialValues={formValues}
      validate={() => {
        onChangeForm();
      }}
    >
      {({ values, handleBlur, handleSubmit, setFieldValue }) => (
        <div className="alert-form">
          <form onSubmit={handleSubmit}>
            <div className="alert-form-header">
              <div className="alert-form-header-left">Notification Management</div>
              <div className="alert-form-header-right">
                {canCreateEditAlert ? (
                  <Button type="submit" className="action-btn">
                    Save
                  </Button>
                ) : null}
                <Button className="close-btn" onClick={() => onCloseForm()}>
                  <Icon type="clear" />
                </Button>
              </div>
            </div>

            <FieldArray
              name="alert_settings"
              render={(arrayHelpers) => (
                <Grid fluid className="alert-form-content">
                  <div className="alert-content-items">
                    {values.alert_settings.map((a, i) => (
                      <Row key={i}>
                        <Row>
                          <Col xs={24}>
                            {userId &&
                              (a.user_id ? (
                                <div className="alert-title">Patient’s Custom Notification</div>
                              ) : (
                                <div className="alert-title">Hospital’s Notification</div>
                              ))}
                            <SelectPickerInput
                              disabled={!canCreateEditAlert || (!!userId && !a.user_id)}
                              searchable={false}
                              name="value_type"
                              data={ALERT_VALUE_TYPE_OPTIONS}
                              onChange={(value) => valueTypeOnChangeHandler(value, values, i, setFieldValue)}
                              labelTitle="Value Type"
                              value={values.alert_settings[i].value_type}
                              placeholder="Value Type"
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={24} className="multiple-inputs-col">
                            If
                            <SelectPickerInput
                              disabled={!canCreateEditAlert || (!!userId && !a.user_id)}
                              searchable={false}
                              name="signal_type"
                              data={ALERT_SIGNAL_TYPE_OPTIONS}
                              onChange={(value) => setFieldValue(`alert_settings.${i}.signal_type`, value)}
                              value={values.alert_settings[i].signal_type}
                            />
                            is
                            <Input
                              disabled={!canCreateEditAlert || (!!userId && !a.user_id)}
                              className={classnames(
                                "alert-form-text-input",
                                `${!canCreateEditAlert || (!!userId && !a.user_id) ? "disabled" : ""}`,
                              )}
                              type="text"
                              name="value"
                              maxLength={6}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setFieldValue(`alert_settings.${i}.value`, e.target.value)
                              }
                              onBlur={handleBlur}
                              value={values.alert_settings[i].value}
                            />
                            {values.alert_settings[i].value_type === ALERT_VALUE_TYPE_OPTIONS[0].value && (
                              <div className="percent-char">%</div>
                            )}
                            <SelectPickerInput
                              disabled={!canCreateEditAlert || (!!userId && !a.user_id)}
                              searchable={false}
                              name="comparison_type"
                              data={ALERT_COMPARISON_TYPE_OPTIONS}
                              onChange={(value) => setFieldValue(`alert_settings.${i}.comparison_type`, value)}
                              value={values.alert_settings[i].comparison_type}
                            />
                            Stable period
                          </Col>
                        </Row>
                        <ErrorMessageField name={`alert_settings.${i}.value`} />
                        <Row>
                          <Col xs={24} className="multiple-inputs-col">
                            Occurs at least
                            <Input
                              disabled={!canCreateEditAlert || (!!userId && !a.user_id)}
                              className={classnames(
                                "alert-form-text-input",
                                `${!canCreateEditAlert || (!!userId && !a.user_id) ? "disabled" : ""}`,
                              )}
                              type="text"
                              name="minimal_count"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setFieldValue(`alert_settings.${i}.minimal_count`, e.target.value)
                              }
                              onBlur={handleBlur}
                              value={values.alert_settings[i].minimal_count}
                            />
                            consecutive days in a row
                          </Col>
                        </Row>
                        <ErrorMessageField name={`alert_settings.${i}.minimal_count`} />
                        <Row>
                          <Col xs={24} className="multiple-inputs-col">
                            Send notification as
                            <SelectPickerInput
                              disabled={!canCreateEditAlert || (!!userId && !a.user_id)}
                              searchable={false}
                              name="alert_type"
                              data={ALERT_TYPE_OPTIONS}
                              onChange={(value) => setFieldValue(`alert_settings.${i}.alert_type`, value)}
                              value={values.alert_settings[i].alert_type}
                            />
                          </Col>
                        </Row>
                        <AccessControl
                          permission={userId ? PERMISSION.MANAGE_CUSTOM_ALERTS : PERMISSION.MANAGE_HOSPITALS_ALERTS}
                        >
                          {userId ? (
                            !a.user_id ? (
                              <Button
                                className={classnames("status-btn", `${a.is_active ? "deactivate" : "activate"}`)}
                                onClick={() => {
                                  setFieldValue(`alert_settings.${i}.is_active`, !values.alert_settings[i].is_active);
                                }}
                              >
                                {a.is_active ? "Deactivate" : "Activate"}
                              </Button>
                            ) : (
                              <div className="alerts-content-remove-item" onClick={() => onDelete(arrayHelpers, i)}>
                                <Icon type="delete" />
                                Delete Notification
                              </div>
                            )
                          ) : (
                            <div className="alerts-content-remove-item" onClick={() => onDelete(arrayHelpers, i)}>
                              <Icon type="delete" />
                              Delete Notification
                            </div>
                          )}
                        </AccessControl>
                        <HorizontalDivider />
                      </Row>
                    ))}
                  </div>
                  <AccessControl
                    permission={userId ? PERMISSION.MANAGE_CUSTOM_ALERTS : PERMISSION.MANAGE_HOSPITALS_ALERTS}
                  >
                    <div className="add-item-wrapper">
                      <Button className="add-item" onClick={() => addNewAlert(arrayHelpers)}>
                        + Add Notification
                      </Button>
                    </div>
                  </AccessControl>
                </Grid>
              )}
            />
          </form>
        </div>
      )}
    </Formik>
  );
};

export default AlertForm;
